import { Button, ButtonVariant, ClickEvent, DataSourceMode, Image, Label, Layout, Panel, Tab, TableRow, TableRowDisplayEvent, TableRowExpansionEvent, TableSelectionEvent, Tabset, Textbox, Toast } from "@mcleod/components";
import { AutogenLayoutCollectionsTable } from "./autogen/AutogenLayoutCollectionsTable";
import { Api, Currency, CurrencySettings, DateUtil, LogManager, ModelRow, StringUtil } from "@mcleod/core";
import { ARCallRecord } from "./ARCallRecord";
import { ARCallHistory } from "./ARCallHistory";
import { LMEGeneralSettings } from "@mcleod/general";
import { CollectionsTableLayout } from "./Collections";
import { RowCollectionsProfile } from "./models/ModelCollectionsProfile";
import { FilterPanel, OrderTabView, SecondaryFilterProps } from "@mcleod/dispatch";
import { CollectionsSecondaryFilter } from "./CollectionsSecondaryFilter";
import { AutogenLayoutCustomerAgingQuickInfo } from "./autogen/AutogenLayoutCustomerAgingQuickInfo";
import { CommonDialogs, ImageTable } from "@mcleod/common";
import { ImageName } from "@mcleod/images";
import { UploadImages } from "@mcleod/common/src/imaging/UploadImages";
import { CollectionsUtil } from "./CollectionsUtil";
import { CollectionsExpand } from "./CollectionsExpand";
import { MiscInvoiceHistory } from "./MiscInvoiceHistory";
import { LabelStyles } from "@mcleod/components/src/components/label/LabelStyles";

const log = LogManager.getLogger("ar.CollectionsTable");

const periodColumnCaptions = [
    { caption: "Period 1", descrField: "age_period_1_desc" },
    { caption: "Period 2", descrField: "age_period_2_desc" },
    { caption: "Period 3", descrField: "age_period_3_desc" },
    { caption: "Period 4", descrField: "age_period_4_desc" }
];

export class CollectionsTable extends AutogenLayoutCollectionsTable implements CollectionsTableLayout {
    public selectedCustomerRow: ModelRow;
    public filterPanel: FilterPanel;
    public getProfileRow: () => RowCollectionsProfile;

    public secondaryFilters: SecondaryFilterProps[] = [
        CollectionsSecondaryFilter.FILTER_CURRENT,
        CollectionsSecondaryFilter.FILTER_1_TO_15_DAYS,
        CollectionsSecondaryFilter.FILTER_16_TO_30_DAYS,
        CollectionsSecondaryFilter.FILTER_OVER_30_DAYS,
        CollectionsSecondaryFilter.FILTER_SHORT_PAYS
    ];

    override onLoad() {
        this.filterPanel = new FilterPanel(this.secondaryFilters, this.tableCollections, this.panelSecondaryFilters);
        this.setColumnHeadersFromARControl();
        this.setFooterCaptionsFromARControl();
        this.setCreditLimitTooltipContent(this.textboxTotalCreditLimit);
        this.setCreditLimitTooltipContent(this.textboxUsedCreditLimit);
        this.setCreditLimitTooltipContent(this.textboxAvailableCreditLimit);
    }

    // START - tableCollections event listeners
    tableCollectionsOnSelect(event: TableSelectionEvent) {
        this.buttonCallEntry.enabled = event.newRows?.length > 0;
    }

    tableCollectionsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;

        tableRow.forEveryChildComponent((component) => {
            CollectionsUtil.setTooltipContent(component, modelRow);
        });

        this.updateCallButtonsAndLabels(tableRow, modelRow);

        const imageRebill = tableRow.findComponentById("imageRebilled") as Image;
        imageRebill.visible = modelRow.get("rebilled", false);

        const imagePendingCashReceipts = tableRow.findComponentById("imagePendingCashReceipt") as Image;
        imagePendingCashReceipts.visible = modelRow.get("has_pending_cash_receipt", false);

        const imageMultipleRecords = tableRow.findComponentById("imageMultipleRecords") as Image;
        imageMultipleRecords.visible = modelRow.get("contains_multiple_items", false);
        imageMultipleRecords.setClassIncluded(LabelStyles.link, true);

        const labelInvoiceNo = tableRow.findComponentById("labelInvoiceNo") as Label;
        labelInvoiceNo.setClassIncluded(LabelStyles.link, true);
    }

    tableCollectionsOnRowExpand(event: TableRowExpansionEvent) {
        this.initializeRecordsTab(event);
        this.initializeImagesTab(event);
    }

    initializeRecordsTab(event: TableRowExpansionEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        const orderId = modelRow.getString("order_id", null);
        const invoiceCurrencyType = modelRow.getString("invoice_currency_type", CurrencySettings.getTransactionalCurrency());
        if (orderId) {
            const expandPanel = event.expandComponentParent as Panel;
            const layoutCollectionsExpandComponent = expandPanel.findComponentById("layoutCollectionsExpand");
            const layoutCollectionsExpand = layoutCollectionsExpandComponent instanceof CollectionsExpand ? layoutCollectionsExpandComponent : null;

            if (layoutCollectionsExpand) {
                layoutCollectionsExpand.invoiceCurrencyType = invoiceCurrencyType;
            }

            if (orderId.toUpperCase() === "OPEN") {
                layoutCollectionsExpand.mainDataSource.search({
                    "open_item.id": modelRow.getString("id", null),
                    invoice_currency_type: invoiceCurrencyType
                });
            } else {
                layoutCollectionsExpand.mainDataSource.search({
                    order_id: orderId,
                    invoice_currency_type: invoiceCurrencyType
                });
            }
        }
    }

    async initializeImagesTab(event: TableRowExpansionEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        const expandComponent = await tableRow.getExpansionComponentLoaded() as Panel;
        const tabset = expandComponent.findComponentById("tabsetCollectionsExpand") as Tabset;
        const imageTable = expandComponent.findComponentById("layoutImageTable") as ImageTable;
        const orderId = modelRow.getString("order_id", null); // open_item.order_id
        const orderIdExists = modelRow.getBoolean("order_id_exists", false); // Only true if open_item.order_id exists also exists in the orders table

        if (tabset && Array.isArray(tabset.components) && !tabset.components.some((tab: Tab) => tab.id === "tabUploadImage")) {
            const uploadButton = new Button({
                id: "buttonUploadImage",
                caption: "Upload Image",
                color: "primary",
                imageName: ImageName.pictureOutlined,
                variant: ButtonVariant.text,
                fontBold: true,
                fontSize: "medium",
                enabled: orderId && orderIdExists,
                disabledTooltip: orderIdExists ? null : `Cannot upload images. Order ID [${orderId}] not found in the [orders] table.`,
                onClick: () => {
                    const layout = Layout.getLayout("common/imaging/UploadImages", {
                        maxWidth: 1100,
                        width: window.innerWidth * .55,
                        backgroundColor: "defaultBackground",
                        borderRadius: 4,
                        borderWidth: 1,
                        borderShadow: true,
                        borderColor: "strokeSecondary"
                    }) as UploadImages;

                    if (orderId && orderIdExists) {
                        const imageDialog = CommonDialogs.createDialog(layout, { title: "Upload Images", okVisible: false });
                        layout.setup("O", orderId, null, () => imageDialog.close());
                        imageDialog.show().then(() => {
                            if (imageTable && layout.addedAnImage) {
                                imageTable.populateForRow("O", orderId);
                            }
                        });
                    }
                }
            });

            tabset.addCenterTool(uploadButton);
            imageTable.buttonUploadImage.visible = false;
        }

        if (orderId && orderIdExists) {
            imageTable.populateForRow("O", orderId);
        }
    }
    // END - tableCollections event listeners

    updateCallButtonsAndLabels(tableRow: TableRow, modelRow: ModelRow) {
        const labelCallCount = tableRow.findComponentById("labelCallCount") as Label;
        const buttonCallHistory = tableRow.findComponentById("buttonCallHistory") as Button;
        const callCount = modelRow.get("call_count", 0);
        labelCallCount.visible = !modelRow.isNull("call_record_id");
        buttonCallHistory.visible = !modelRow.isNull("call_record_id");
        buttonCallHistory.tooltip = callCount === 1 ? "View Call Record" : "View Call History";
    }

    buttonCallEntryOnClick() {
        const selectedRows = this.tableCollections.selectedRows.map(row => row.data as ModelRow);
        ARCallRecord.showSlideout({
            customerId: this.selectedCustomerRow.getString("customer_id", null),
            customerName: this.selectedCustomerRow.getString("customer_name", null),
            customerActionCode: this.selectedCustomerRow.getString("customer_action_code", null),
            customerActionCodeDescr: this.selectedCustomerRow.getString("customer_action_code_descr", null),
            defaultNextActionCode: this.selectedCustomerRow.getString("default_next_action_code", null), // Default next action values are used on new call records.
            defaultNextActionDays: this.selectedCustomerRow.getNumber("default_next_action_days", -1), // They come from the customer's next action code's core file.
            defaultNextActionDescr: this.selectedCustomerRow.getString("default_next_action_descr", null),
            discussedItems: selectedRows,
            doAfterSlideOut: (success: boolean = false, callRecordId?: string) => {
                if (success) {
                    Toast.showSuccessToast("Call detail saved successfully");
                    if (callRecordId) {
                        this.tableCollections.selectedRows.forEach(row => {
                            const modelRow = row.data as ModelRow;
                            modelRow.set("call_record_id", callRecordId);
                            modelRow.set("call_count", modelRow.getNumber("call_count", 0) + 1);
                            this.updateCallButtonsAndLabels(row, modelRow);
                            row.updateBoundData(modelRow, DataSourceMode.NONE);
                        });
                    } else {
                        log.debug("No call_record_id returned from ARCallRecord slideout. Could not update call counts.");
                    }
                }
            }
        });
    }

    // START - Call History row button logic
    buttonCallHistoryOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Button);
        const callCount = tableRow.data?.get("call_count", 0);

        if (callCount == 1) {
            this.showSingleCallRecordInSlideout(tableRow);
        } else if (callCount > 1) {
            this.showCallHistoryInSlideout(tableRow);
        }
    }

    showSingleCallRecordInSlideout(tableRow: TableRow) {
        const customerId = tableRow.data?.getString("customer_id", null);
        const customerName = tableRow.data?.getString("customer_name", null);
        const callRecordId = tableRow.data?.getString("call_record_id", null);
        ARCallRecord.showSlideout({
            customerId: customerId,
            customerName: customerName,
            callRecordId: callRecordId,
            doAfterSlideOut: (success: boolean = false) => {
                if (success) {
                    Toast.showSuccessToast("Call detail saved successfully");
                }
            }
        });
    }

    showCallHistoryInSlideout(tableRow: TableRow) {
        const customerId = tableRow.data?.getString("customer_id", null);
        const customerName = tableRow.data?.getString("customer_name", null);
        const orderId = tableRow.data?.getString("order_id", null);
        ARCallHistory.showSlideout({
            showSearchPanel: false,
            customerId: customerId,
            customerName: customerName,
            orderId: orderId
        });
    }
    // END - Call History row button logic

    labelInvoiceNoOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Label);
        const modelRow = tableRow.data as ModelRow;
        const invoiceNo = modelRow.getString("invoice_no", null);
        const source = modelRow.getString("source", null);

        // Source 'A' indicates a miscellaneous invoice
        if (source === "A" && invoiceNo) {
            this.handleMiscInvoiceClick(invoiceNo);
        } else {
            const orderId = modelRow.getString("order_id", null);
            this.handleNonMiscInvoiceClick(orderId);
        }
    }

    private handleMiscInvoiceClick(invoiceNo: string) {
        MiscInvoiceHistory.slideUp({
            invoiceNoString: invoiceNo
        });
    }

    private handleNonMiscInvoiceClick(orderId: string | null) {
        if (orderId) {
            OrderTabView.slideUp({
                key: orderId
            });
        }
    }

    private setColumnHeadersFromARControl() {
        const arControl = LMEGeneralSettings.getSingleton().getARControl();
        for (const { caption, descrField } of periodColumnCaptions) {
            const column = this.tableCollections.getColumnByCaption(caption);
            if (column && column.headingCell) {
                const label = column.headingCell.findComponentByType(Label);
                if (label) {
                    label.text = arControl[descrField];
                    label.tooltip = arControl[descrField];
                }
            }
        }
    }

    private setFooterCaptionsFromARControl() {
        const arControl = LMEGeneralSettings.getSingleton().getARControl();
        this.textboxPeriod1Total.caption = arControl["age_period_1_desc"] ?? "Period 1";
        this.textboxPeriod2Total.caption = arControl["age_period_2_desc"] ?? "Period 2";
        this.textboxPeriod3Total.caption = arControl["age_period_3_desc"] ?? "Period 3";
        this.textboxPeriod4Total.caption = arControl["age_period_4_desc"] ?? "Period 4";
    }

    async updateTableFooter() {
        const selectedCustomerId = this.selectedCustomerRow?.getString("customer_id", null);
        if (selectedCustomerId != null) {
            await Promise.all([
                this.updateFooterCreditLimit(),
                this.updateFooterTerms(),
                this.updateFooterPayments(),
                this.updateFooterSummary()
            ]);
        } else {
            this.clearFooterData();
        }
    }

    async updateFooterCreditLimit() {
        const selectedCustomerId = this.selectedCustomerRow?.getString("customer_id", null);
        this.panelCreditLimitValues.busy = true;
        await this.sourceCustomerAging.search({ customer_id: selectedCustomerId });
        this.panelCreditLimitValues.busy = false;
    }

    updateFooterTerms() {
        const arControl = LMEGeneralSettings.getSingleton().getARControl();
        const customerDays = this.selectedCustomerRow?.getNumber("customer_bill_due_days", -1);
        const fromARControl = customerDays < 0;
        const billDueDays = fromARControl
            ? (arControl["bill_due_days"] ?? 0) as number
            : customerDays;

        this.textboxBillDueDays.text = billDueDays.toString();
        this.textboxBillDueDays.tooltip = fromARControl ? "AR Control Setting" : "Customer Setting";
    }

    async updateFooterPayments() {
        const selectedCustomerId = this.selectedCustomerRow?.getString("customer_id", null);
        this.panelPaymentsValues.busy = true;
        const response = await Api.post("lme/ar/get-customer-last-payment", { customer_id: selectedCustomerId,
            customer_currency: this.selectedCustomerRow?.getString("customer_currency", CurrencySettings.getTransactionalCurrency()) });

        if (response && response.data[0]) {
            if (response.data[0].last_payment_date != null) {
                const lastPaymentDate = DateUtil.parseDate(response.data[0].last_payment_date as Date);
                this.textboxLastPaymentDate.text = DateUtil.formatDate(lastPaymentDate, "MM/dd/yyyy");
            } else {
                this.textboxLastPaymentDate.text = null;
            }

            if (response.data[0].last_payment_amount != null) {
                const lastPaymentAmount = new Currency({ ...(response.data[0].last_payment_amount as Currency) });
                this.textboxLastPaymentAmount.text = lastPaymentAmount.toString();
            } else {
                this.textboxLastPaymentAmount.text = null;
            }
        } else {
            this.textboxLastPaymentDate.text = null;
            this.textboxLastPaymentAmount.text = null;
        }

        const customerAveragePayDays = this.selectedCustomerRow?.getNumber("customer_average_pay_days", null);
        this.textboxAveragePayDays.text = customerAveragePayDays !== null && customerAveragePayDays >= 0 ? customerAveragePayDays.toString() : null;

        this.panelPaymentsValues.busy = false;
    }

    async updateFooterSummary() {
        const customerCurrency = this.selectedCustomerRow?.getString("customer_currency", CurrencySettings.getTransactionalCurrency());

        const rows = this.tableCollections.filteredRows?.map(row => row.data as ModelRow) ?? [];
        const openCreditsTotal = new Currency({ amount: 0, currency_code: customerCurrency, conversion_date: new Date() });
        const currentPeriodTotal = new Currency({ amount: 0, currency_code: customerCurrency, conversion_date: new Date() });
        const period1Total = new Currency({ amount: 0, currency_code: customerCurrency, conversion_date: new Date() });
        const period2Total = new Currency({ amount: 0, currency_code: customerCurrency, conversion_date: new Date() });
        const period3Total = new Currency({ amount: 0, currency_code: customerCurrency, conversion_date: new Date() });
        const period4Total = new Currency({ amount: 0, currency_code: customerCurrency, conversion_date: new Date() });

        for (const row of rows) {
            const openCreditsAmount = row.getString("order_id", "").toUpperCase() === "OPEN" ? row.getCurrency("balance", null) : null;
            const currentPeriodAmount = row.getCurrency("current_amount", null);
            const period1Amount = row.getCurrency("period1_amount", null);
            const period2Amount = row.getCurrency("period2_amount", null);
            const period3Amount = row.getCurrency("period3_amount", null);
            const period4Amount = row.getCurrency("period4_amount", null);

            await Promise.all([
                openCreditsAmount && openCreditsTotal.add(openCreditsAmount),
                currentPeriodAmount && currentPeriodTotal.add(currentPeriodAmount),
                period1Amount && period1Total.add(period1Amount),
                period2Amount && period2Total.add(period2Amount),
                period3Amount && period3Total.add(period3Amount),
                period4Amount && period4Total.add(period4Amount)
            ]);
        }

        this.textboxOpenCreditsTotalBalance.text = openCreditsTotal?.toString();
        this.textboxCurrentPeriodTotal.text = currentPeriodTotal?.toString();
        this.textboxPeriod1Total.text = period1Total?.toString();
        this.textboxPeriod2Total.text = period2Total?.toString();
        this.textboxPeriod3Total.text = period3Total?.toString();
        this.textboxPeriod4Total.text = period4Total?.toString();
    }

    clearFooterData() {
        this.sourceCustomerAging.clear();
        this.textboxTotalCreditLimit.text = null;
        this.textboxUsedCreditLimit.text = null;
        this.textboxAvailableCreditLimit.text = null;
        this.textboxBillDueDays.text = null;
        this.textboxLastPaymentDate.text = null;
        this.textboxLastPaymentAmount.text = null;
        this.textboxAveragePayDays.text = null;
        this.textboxOpenCreditsTotalBalance.text = null;
        this.textboxCurrentPeriodTotal.text = null;
        this.textboxPeriod1Total.text = null;
        this.textboxPeriod2Total.text = null;
        this.textboxPeriod3Total.text = null;
        this.textboxPeriod4Total.text = null;
    }

    private setCreditLimitTooltipContent(component: Textbox) {
        component.tooltipDynamicContent = async () => {
            const customerId = this.selectedCustomerRow?.getString("customer_id", null);
            if (!StringUtil.isEmptyString(customerId)) {
                const layout = await Layout.getLoadedLayout("lme/ar/CustomerAgingQuickInfo") as AutogenLayoutCustomerAgingQuickInfo;
                await layout.mainDataSource?.search({ customer_id: customerId });
                return layout;
            }
            return null;
        };
    }
}
